import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  Box, Paper, Typography, Button, CircularProgress, Chip, Grid, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Alert, IconButton, Tooltip, InputAdornment,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import PendingIcon from '@mui/icons-material/HourglassEmptyOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import SyncIcon from '@mui/icons-material/SyncOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import PublicIcon from '@mui/icons-material/PublicOutlined';
import { timesheetAPI, verificationsAPI, wrikeAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatHM, parseToMinutes } from '../utils/time';

// Minutes for a row, preferring the exact server value, falling back to derived hours
const rowMinutes = (row) => row.actual_minutes != null ? row.actual_minutes : Math.round((row.actual_hours || 0) * 60);
const verMinutes = (ver) => ver?.verified_minutes != null
  ? ver.verified_minutes
  : (ver?.verified_hours != null ? Math.round(parseFloat(ver.verified_hours) * 60) : null);

const TH = { fontSize: '0.8rem', fontWeight: 600, color: 'text.secondary', py: 1.5, px: 2 };
const TD = { fontSize: '0.875rem', color: 'text.primary', py: 1.25, px: 2 };

const CURRENCY_SYMBOLS = { USD: '$', PHP: '₱', BDT: '৳' };
function fmt(n, currency = 'PHP') {
  const sym = CURRENCY_SYMBOLS[currency] || currency;
  return `${sym}${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusChip({ status }) {
  const map = {
    verified: { label: 'Verified', color: '#2e9466', bg: '#10b98115', icon: <CheckCircleIcon sx={{ fontSize: 13 }} /> },
    rejected: { label: 'Rejected', color: '#d04a40', bg: '#ef444415', icon: <CancelIcon      sx={{ fontSize: 13 }} /> },
    pending:  { label: 'Pending',  color: '#d08c1e', bg: '#f59e0b15', icon: <PendingIcon      sx={{ fontSize: 13 }} /> },
  };
  const s = map[status] || map.pending;
  return (
    <Chip icon={s.icon} label={s.label} size="small"
      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: '0.72rem', '& .MuiChip-icon': { color: s.color } }} />
  );
}

function EditRow({ row, periodId, onSaved }) {
  const { hideAmounts } = useAuth();
  // Cash advance is a payroll deduction, so it follows the amount-visibility flag.
  const canSetCashAdvance = !hideAmounts;
  const sym = CURRENCY_SYMBOLS[row.employee?.currency] || row.employee?.currency || '₱';
  // Editable field holds "Hh Mm" text; the source of truth sent to the API is minutes.
  const initialMinutes = verMinutes(row.verification) ?? rowMinutes(row);
  const [hours, setHours]   = useState(initialMinutes ? formatHM(initialMinutes) : '');
  const [cash,  setCash]    = useState(row.verification?.cash_advance ?? 0);
  const [notes, setNotes]   = useState(row.verification?.notes ?? '');
  const [saving, setSaving] = useState(false);

  const save = async (newStatus) => {
    setSaving(true);
    try {
      await verificationsAPI.upsert({
        employee_id:      row.employee.id,
        period_id:        periodId,
        verified_minutes: hours !== '' ? parseToMinutes(hours) : null,
        ...(canSetCashAdvance ? { cash_advance: parseFloat(cash) || 0 } : {}),
        status:           newStatus,
        notes,
      });
      toast.success(`${row.employee.name} marked as ${newStatus}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <TextField label="Hours (e.g. 8h 30m)" size="small" value={hours} onChange={e => setHours(e.target.value)}
        placeholder="8h 30m" sx={{ width: 130 }} />
      {canSetCashAdvance && (
        <TextField label="Cash Advance" type="number" size="small" value={cash} onChange={e => setCash(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">{sym}</InputAdornment> }}
          inputProps={{ step: 100, min: 0 }} sx={{ width: 150 }} />
      )}
      <TextField label="Notes" size="small" value={notes} onChange={e => setNotes(e.target.value)} sx={{ width: 200 }} />
      <Button size="small" variant="contained" disabled={saving} onClick={() => save('verified')}
        startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
        sx={{ textTransform: 'none', bgcolor: '#2e9466', '&:hover': { bgcolor: '#237a53' }, borderRadius: '8px', fontSize: '0.78rem' }}>
        Verify
      </Button>
      <Button size="small" variant="outlined" disabled={saving} onClick={() => save('rejected')}
        startIcon={<CancelIcon />}
        sx={{ textTransform: 'none', borderColor: '#d04a40', color: '#d04a40', borderRadius: '8px', fontSize: '0.78rem',
          '&:hover': { borderColor: '#b83a31', bgcolor: '#ef444410' } }}>
        Reject
      </Button>
    </Box>
  );
}

export default function GenerateTimesheet() {
  const { user } = useAuth();
  const isReadOnly = user?.role === 'accounting_manager';

  const [periods,        setPeriods]        = useState([]);
  const [periodsLoading, setPeriodsLoading] = useState(true);
  const [periodTab,      setPeriodTab]      = useState(0); // 0=local, 1=foreign
  const [periodSearch,   setPeriodSearch]   = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [bulking,   setBulking]   = useState(false);
  const [syncing,   setSyncing]   = useState(false);
  const [empSearch,     setEmpSearch]     = useState('');
  const [payslipEmpIds, setPayslipEmpIds] = useState(new Set());

  useEffect(() => {
    timesheetAPI.getPeriods(200, 0)
      .then(res => setPeriods(res.data || []))
      .catch(() => toast.error('Failed to load periods'))
      .finally(() => setPeriodsLoading(false));
  }, []);

  const fetchVerifications = useCallback(async (periodId) => {
    if (!periodId) return;
    setLoading(true);
    setEditingId(null);
    try {
      const [verRes, payRes] = await Promise.all([
        verificationsAPI.getForPeriod(periodId),
        timesheetAPI.getPeriodPayslips(periodId),
      ]);
      setData(verRes.data || []);
      const ids = new Set((payRes.data || []).map(p => p.employee_id));
      setPayslipEmpIds(ids);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load verification data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVerifications(selectedPeriod?.id);
    setEmpSearch('');
  }, [selectedPeriod, fetchVerifications]);

  const handleSyncWrike = async () => {
    setSyncing(true);
    try {
      const res = await wrikeAPI.importPeriod(selectedPeriod.id);
      const { imported, updated = 0, deleted = 0, skipped } = res.data || res;
      toast.success(`Synced from Wrike: ${imported} new, ${updated} corrected, ${deleted} removed, ${skipped} unchanged`, { duration: 6000 });
      fetchVerifications(selectedPeriod.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Wrike sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleBulkVerify = async () => {
    const eligible = data.filter(r => r.actual_hours > 0 && r.status !== 'verified');
    if (!eligible.length) { toast('All employees with hours are already verified.', { icon: 'ℹ️' }); return; }
    setBulking(true);
    try {
      await verificationsAPI.bulk(selectedPeriod.id, eligible.map(r => r.employee.id), 'verified');
      toast.success(`Verified ${eligible.length} employees`);
      fetchVerifications(selectedPeriod.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk verify failed');
    } finally {
      setBulking(false);
    }
  };

  // Period list filtering
  const localPeriods   = periods.filter(p => !p.period_type || p.period_type === 'local');
  const foreignPeriods = periods.filter(p => p.period_type === 'foreign');
  const tabPeriods     = periodTab === 0 ? localPeriods : foreignPeriods;
  const filteredPeriods = periodSearch
    ? tabPeriods.filter(p => p.period_name.toLowerCase().includes(periodSearch.toLowerCase()))
    : tabPeriods;

  // Employee table
  const withHours     = data.filter(r => r.actual_hours > 0).length;
  const verifiedCount = data.filter(r => r.actual_hours > 0 && r.status === 'verified').length;
  const pendingCount  = data.filter(r => r.actual_hours > 0 && r.status === 'pending').length;
  const rejectedCount = data.filter(r => r.actual_hours > 0 && r.status === 'rejected').length;
  const visibleData   = data
    .filter(r => r.actual_hours > 0)
    .filter(r => !empSearch || r.employee?.name?.toLowerCase().includes(empSearch.toLowerCase()));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'linear-gradient(135deg, #2a7f96, #4da3ba)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FactCheckIcon sx={{ color: 'white', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              Verify Timesheet
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              Review and confirm employee hours before processing payroll
            </Typography>
          </Box>
        </Box>
        {selectedPeriod && !isReadOnly && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={handleSyncWrike} disabled={syncing}
              startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
              sx={{ textTransform: 'none', borderRadius: '10px', fontSize: '0.82rem', borderColor: '#2a7f96', color: '#2a7f96',
                '&:hover': { borderColor: '#1f6e84', bgcolor: '#6366f110' } }}>
              {syncing ? 'Syncing…' : 'Sync Wrike'}
            </Button>
            <Tooltip title={payslipEmpIds.size === data.length && data.length > 0 ? 'All payslips already generated — nothing to verify' : ''} arrow>
              <span>
                <Button variant="outlined" onClick={handleBulkVerify}
                  disabled={bulking || !data.length || (payslipEmpIds.size === data.length && data.length > 0)}
                  startIcon={bulking ? <CircularProgress size={14} /> : <FactCheckIcon />}
                  sx={{ textTransform: 'none', borderRadius: '10px', fontSize: '0.82rem', borderColor: '#2e9466', color: '#2e9466',
                    '&:hover': { borderColor: '#237a53', bgcolor: '#10b98110' } }}>
                  {bulking ? 'Verifying…' : `Verify All with Hours (${withHours})`}
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Grid container spacing={2} sx={{ alignItems: 'start' }}>

        {/* ── Left: Period list ──────────────────────────────────────────── */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'divider', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

            {/* Tabs */}
            <Tabs value={periodTab} onChange={(_, v) => { setPeriodTab(v); setSelectedPeriod(null); setPeriodSearch(''); }}
              sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 44,
                '& .MuiTabs-indicator': { bgcolor: '#2a7f96', height: 3 },
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', minHeight: 44, py: 0 } }}>
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <HomeIcon sx={{ fontSize: 14 }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1 }}>Local</Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1 }}>2× / month</Typography>
                  </Box>
                  <Chip label={localPeriods.length} size="small"
                    sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: periodTab === 0 ? '#6366f115' : 'action.hover', color: periodTab === 0 ? '#2a7f96' : 'text.secondary' }} />
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <PublicIcon sx={{ fontSize: 14 }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1 }}>International</Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1 }}>1× / month</Typography>
                  </Box>
                  <Chip label={foreignPeriods.length} size="small"
                    sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: periodTab === 1 ? '#6366f115' : 'action.hover', color: periodTab === 1 ? '#2a7f96' : 'text.secondary' }} />
                </Box>
              } />
            </Tabs>

            {/* Search */}
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
              <TextField fullWidth size="small" placeholder="Search periods…" value={periodSearch} onChange={e => setPeriodSearch(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.82rem' } }}
                slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} /></InputAdornment> } }} />
            </Box>

            {/* Period list */}
            <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
              {periodsLoading ? (
                <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}><CircularProgress size={22} sx={{ color: '#2a7f96' }} /></Box>
              ) : filteredPeriods.length === 0 ? (
                <Box sx={{ py: 5, textAlign: 'center', color: 'text.disabled', fontSize: '0.8rem' }}>
                  {periodSearch ? 'No periods match your search' : 'No periods'}
                </Box>
              ) : filteredPeriods.map(p => {
                const active = selectedPeriod?.id === p.id;
                const statusLabel = { open: 'Open', pending_approval: 'Pending Approval', approved: 'Approved', completed: 'Completed', paid: 'Paid' }[p.status] || p.status;
                const statusColor = { open: '#2a7f96', pending_approval: '#d08c1e', approved: '#2e9466', completed: '#2e9466', paid: '#2e9466' }[p.status] || '#2a7f96';
                return (
                  <Box key={p.id} onClick={() => setSelectedPeriod(p)}
                    sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderBottomColor: 'divider', cursor: 'pointer',
                      bgcolor: active ? 'rgba(42,127,150,0.06)' : 'transparent',
                      borderLeft: active ? '3px solid #2a7f96' : '3px solid transparent',
                      transition: 'all 0.15s', '&:hover': { bgcolor: active ? 'rgba(42,127,150,0.08)' : 'action.hover' } }}>
                    <Typography sx={{ fontWeight: active ? 700 : 500, fontSize: '0.8rem', color: active ? '#2a7f96' : 'text.primary',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.25 }}>
                      {p.period_name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', mb: 0.4 }}>
                      {fmtDate(p.start_date)} – {fmtDate(p.end_date)}
                    </Typography>
                    <Chip label={statusLabel} size="small"
                      sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: `${statusColor}18`, color: statusColor }} />
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        {/* ── Right: Employee table ──────────────────────────────────────── */}
        <Grid size={{ xs: 12, md: 9 }}>
          {!selectedPeriod ? (
            <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'divider', py: 14, textAlign: 'center', color: 'text.disabled' }}>
              <CalendarMonthIcon sx={{ fontSize: 40, opacity: 0.2, mb: 1.5 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 0.5 }}>Select a Pay Period</Typography>
              <Typography sx={{ fontSize: '0.8rem' }}>Choose a period from the left to review employee hours</Typography>
            </Paper>
          ) : loading ? (
            <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'divider', py: 10, textAlign: 'center' }}>
              <CircularProgress size={32} sx={{ color: '#2a7f96', mb: 2 }} />
              <Typography sx={{ fontSize: '0.875rem', color: 'text.disabled' }}>Loading employee data…</Typography>
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>

              {/* Period header */}
              <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid', borderBottomColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>{selectedPeriod.period_name}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                    {fmtDate(selectedPeriod.start_date)} – {fmtDate(selectedPeriod.end_date)}
                  </Typography>
                </Box>
                {data.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {[
                      { label: `${verifiedCount} Verified`, color: '#2e9466' },
                      { label: `${pendingCount} Pending`,   color: '#d08c1e' },
                      { label: `${rejectedCount} Rejected`, color: '#d04a40' },
                    ].filter(s => {
                      const count = parseInt(s.label);
                      return count > 0;
                    }).map(s => (
                      <Chip key={s.label} label={s.label} size="small"
                        sx={{ bgcolor: `${s.color}15`, color: s.color, fontWeight: 700, fontSize: '0.72rem' }} />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Payslip lock banner */}
              {payslipEmpIds.size > 0 && (
                <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5,
                  bgcolor: payslipEmpIds.size === data.length ? '#10b9810a' : '#6366f10a',
                  borderBottom: '1px solid', borderBottomColor: payslipEmpIds.size === data.length ? '#10b98125' : '#6366f125' }}>
                  <CheckCircleIcon sx={{ fontSize: 16, color: payslipEmpIds.size === data.length ? '#2e9466' : '#2a7f96', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.8rem', color: payslipEmpIds.size === data.length ? '#1b5e40' : '#154c5c', flex: 1 }}>
                    {payslipEmpIds.size === data.length
                      ? <><strong>All payslips generated</strong> for this period. Editing is locked to protect generated payslips.</>
                      : <><strong>{payslipEmpIds.size} of {data.length} employees</strong> already have payslips — those rows are locked. Remaining employees can still be verified.</>
                    }
                  </Typography>
                </Box>
              )}

              {data.length === 0 ? (
                <Box sx={{ py: 10, textAlign: 'center', color: 'text.disabled' }}>
                  <FactCheckIcon sx={{ fontSize: 36, opacity: 0.2, mb: 1.5 }} />
                  <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 0.5 }}>No Employees Found</Typography>
                  <Typography sx={{ fontSize: '0.8rem' }}>No employee timesheet data for this period. Try syncing from Wrike.</Typography>
                </Box>
              ) : (
                <>
                  <Alert severity="info" sx={{ m: 2, borderRadius: '10px', fontSize: '0.78rem', py: 0.75 }}>
                    Review each employee's hours. Override if needed, add cash advance, then <strong>Verify</strong>. Only verified employees appear in Process Payroll.
                  </Alert>

                  {/* Employee search */}
                  <Box sx={{ px: 2, pb: 1.5 }}>
                    <TextField fullWidth size="small" placeholder="Search employees…" value={empSearch} onChange={e => setEmpSearch(e.target.value)}
                      sx={{ maxWidth: 320, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.82rem' } }}
                      slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> } }} />
                  </Box>

                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'action.hover' }}>
                        <TableRow>
                          <TableCell sx={{ ...TH, minWidth: 180 }}>Employee</TableCell>
                          <TableCell sx={{ ...TH, textAlign: 'center', minWidth: 100 }}>Actual Hours</TableCell>
                          <TableCell sx={{ ...TH, textAlign: 'center', minWidth: 110 }}>Verified Hours</TableCell>
                          <TableCell sx={{ ...TH, textAlign: 'center', minWidth: 120 }}>Cash Advance</TableCell>
                          <TableCell sx={{ ...TH, textAlign: 'center', minWidth: 100 }}>Status</TableCell>
                          {!isReadOnly && <TableCell sx={{ ...TH, minWidth: 320 }}>Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleData.map(row => {
                          const emp    = row.employee;
                          const ver    = row.verification;
                          const isEdit = editingId === emp.id;
                          const hasHours = row.actual_hours > 0;
                          return (
                            <React.Fragment key={emp.id}>
                              <TableRow sx={{
                                bgcolor: row.status === 'verified' ? '#10b98105' : row.status === 'rejected' ? '#ef444405' : 'transparent',
                                opacity: hasHours ? 1 : 0.55,
                                '&:hover': { bgcolor: 'action.hover' },
                              }}>
                                <TableCell sx={TD}>
                                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{emp.name}</Typography>
                                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                                    {emp.employee_id}{emp.department ? ` · ${emp.department}` : ''}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ ...TD, textAlign: 'center' }}>
                                  {hasHours
                                    ? <Typography sx={{ fontWeight: 700, color: '#2a7f96' }}>{formatHM(rowMinutes(row))}</Typography>
                                    : <Typography sx={{ color: 'text.disabled', fontSize: '0.8rem' }}>No data</Typography>}
                                </TableCell>
                                <TableCell sx={{ ...TD, textAlign: 'center' }}>
                                  {verMinutes(ver) != null ? (
                                    <Typography sx={{ fontWeight: 700, color: verMinutes(ver) !== rowMinutes(row) ? '#d08c1e' : 'text.primary' }}>
                                      {formatHM(verMinutes(ver))}
                                      {verMinutes(ver) !== rowMinutes(row) && (
                                        <Typography component="span" sx={{ fontSize: '0.65rem', color: '#d08c1e', ml: 0.5 }}>override</Typography>
                                      )}
                                    </Typography>
                                  ) : <Typography sx={{ color: 'text.disabled', fontSize: '0.8rem' }}>—</Typography>}
                                </TableCell>
                                <TableCell sx={{ ...TD, textAlign: 'center' }}>
                                  {ver?.cash_advance > 0
                                    ? <Typography sx={{ fontWeight: 700, color: '#d04a40' }}>{fmt(ver.cash_advance, emp.currency)}</Typography>
                                    : <Typography sx={{ color: 'text.disabled', fontSize: '0.8rem' }}>—</Typography>}
                                </TableCell>
                                <TableCell sx={{ ...TD, textAlign: 'center' }}>
                                  <StatusChip status={row.status} />
                                </TableCell>
                                {!isReadOnly && (
                                  <TableCell sx={TD}>
                                    {payslipEmpIds.has(emp.id) ? (
                                      // Payslip already generated — lock this row
                                      <Tooltip title="Payslip already generated for this employee. Delete the payslip first to make changes." arrow>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                          <CheckCircleIcon sx={{ fontSize: 14, color: '#2e9466' }} />
                                          <Typography sx={{ fontSize: '0.78rem', color: '#2e9466', fontWeight: 600 }}>
                                            Payslip Generated
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    ) : isEdit ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <EditRow row={row} periodId={selectedPeriod.id}
                                          onSaved={() => { setEditingId(null); fetchVerifications(selectedPeriod.id); }} />
                                        <Tooltip title="Cancel">
                                          <IconButton size="small" onClick={() => setEditingId(null)}>
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    ) : row.status === 'verified' ? (
                                      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <CheckCircleIcon sx={{ fontSize: 14, color: '#2e9466' }} />
                                          <Typography sx={{ fontSize: '0.78rem', color: '#2e9466', fontWeight: 600 }}>Verified</Typography>
                                        </Box>
                                        <Tooltip title="Reset to pending so it can be edited again" arrow>
                                          <Button size="small" variant="text"
                                            onClick={async () => {
                                              try {
                                                await verificationsAPI.upsert({ employee_id: emp.id, period_id: selectedPeriod.id, status: 'pending' });
                                                fetchVerifications(selectedPeriod.id);
                                              } catch { toast.error('Failed to reset'); }
                                            }}
                                            sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.disabled', '&:hover': { color: '#d08c1e' } }}>
                                            Reset
                                          </Button>
                                        </Tooltip>
                                      </Box>
                                    ) : (
                                      <Tooltip title="Edit hours and verify">
                                        <span>
                                          <Button size="small" variant="outlined"
                                            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                                            onClick={() => setEditingId(emp.id)}
                                            sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.75rem',
                                              borderColor: '#2a7f96', color: '#2a7f96',
                                              '&:hover': { borderColor: '#1f6e84', bgcolor: '#6366f110' } }}>
                                            Edit & Verify
                                          </Button>
                                        </span>
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Summary footer */}
                  <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderTopColor: 'divider', bgcolor: 'action.hover', display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      <strong style={{ color: '#2e9466' }}>{verifiedCount}</strong> of {withHours} employees verified
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      <strong>{formatHM(data.filter(r => r.actual_hours > 0).reduce((s, r) => s + (verMinutes(r.verification) ?? rowMinutes(r)), 0))}</strong> total hours
                    </Typography>
                    {verifiedCount === withHours && withHours > 0 && (
                      <Chip label="All ready — go to Process Payroll" size="small" icon={<CheckCircleIcon />}
                        sx={{ bgcolor: '#10b98115', color: '#2e9466', fontWeight: 700, fontSize: '0.72rem', '& .MuiChip-icon': { color: '#2e9466' } }} />
                    )}
                  </Box>
                </>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
